import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SidebarLayout from '../components/SidebarLayout';
import { apiClient } from '../lib/api';
import { useGoalPolicy, useGoalOutcomes, useSpanTree } from '../hooks/useAgentDetail';
import { ProviderLogo } from '../components/ui/ProviderLogo';

// --- GoalStatusBadge ---
function GoalStatusBadge({ status }) {
  const map = {
    optimizing: { label: "optimized", bg: "rgba(16,185,129,0.13)", color: "#10b981" },
    leader: { label: "optimized", bg: "rgba(16,185,129,0.13)", color: "#10b981" },
    exploring: { label: "exploring", bg: "rgba(96,165,250,0.13)", color: "#60a5fa" },
    low_data: { label: "exploring", bg: "rgba(96,165,250,0.13)", color: "#60a5fa" },
    converging: { label: "converging", bg: "rgba(245,158,11,0.13)", color: "#f59e0b" },
  };
  const m = map[status] || { label: status || "unknown", bg: "rgba(255,255,255,0.06)", color: "#888" };
  return (
    <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 3, fontFamily: "JetBrains Mono", fontSize: 10.5, fontWeight: 500, background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
}

// --- RoutingCallout skeleton ---
function RoutingCalloutSkeleton() {
  return (
    <div style={{ background: "rgba(16,185,129,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, padding: "16px 20px", marginBottom: 20 }}>
      <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: "#555", marginBottom: 12 }}>Routing decision</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, padding: "12px 14px", height: 80 }} />
        <div style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, padding: "12px 14px", height: 80 }} />
      </div>
    </div>
  );
}

// --- RoutingCallout ---
function RoutingCallout({ policy, stats }) {
  if (!policy && !stats) return <RoutingCalloutSkeleton />;

  const chosen = {
    model: policy?.recommended_model || stats?.paths?.find(p => p.status === "leader")?.model_id || "\u2014",
    successRate: policy?.outcome_success_rate != null
      ? (policy.outcome_success_rate * 100).toFixed(1) + "%"
      : stats?.paths?.find(p => p.status === "leader")
        ? ((stats.paths.find(p => p.status === "leader").success_rate || 0) * 100).toFixed(1) + "%"
        : "\u2014",
    confidence: policy?.confidence != null ? (policy.confidence * 100).toFixed(0) + "%" : "\u2014",
    riskScore: policy?.risk_score != null ? policy.risk_score.toFixed(2) : "\u2014",
    reasoning: policy?.reasoning || null,
  };

  const alternatives = policy?.alternatives || [];

  return (
    <div style={{ background: "rgba(16,185,129,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, padding: "16px 20px", marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: "#555" }}>Routing decision</span>
        <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {/* Chosen path */}
        <div style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, padding: "12px 14px" }}>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555", marginBottom: 8 }}>Chosen path</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 13, fontWeight: 500, color: "#10b981" }}>{chosen.model}</span>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#555" }}>{chosen.successRate} reliability</span>
          </div>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: "#555", marginBottom: chosen.reasoning ? 8 : 0 }}>
            {chosen.confidence} confidence &middot; risk {chosen.riskScore}
          </div>
          {chosen.reasoning && (
            <div style={{ fontSize: 12.5, color: "#888", lineHeight: 1.7, marginTop: 8 }}>{chosen.reasoning}</div>
          )}
        </div>
        {/* Alternatives */}
        <div style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, padding: "12px 14px" }}>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555", marginBottom: 8 }}>Alternatives considered</div>
          {alternatives.length === 0 ? (
            <div style={{ fontSize: 12, color: "#555" }}>No alternatives data yet</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {alternatives.slice(0, 4).map((alt) => (
                <div key={alt.model_id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 11.5, color: "#c0c0c0", flex: "0 0 140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{alt.model_id}</span>
                  <div style={{ flex: 1, height: 3, background: "#222", borderRadius: 2, overflow: "hidden", maxWidth: 80 }}>
                    <div style={{ height: "100%", background: "#555", borderRadius: 2, width: alt.outcome_success_rate != null ? `${(alt.outcome_success_rate * 100).toFixed(0)}%` : "0%" }} />
                  </div>
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 10.5, color: "#555", minWidth: 30, textAlign: "right" }}>{alt.outcome_success_rate != null ? `${(alt.outcome_success_rate * 100).toFixed(0)}%` : "\u2014"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- ToolRow ---
function ToolRow({ span }) {
  const [expanded, setExpanded] = React.useState(false);

  const statusColors = {
    success: { bg: "rgba(16,185,129,0.13)", text: "#10b981" },
    error: { bg: "rgba(248,113,113,0.12)", text: "#f87171" },
    timeout: { bg: "rgba(245,158,11,0.13)", text: "#f59e0b" },
  };
  const sc = statusColors[span.status] || { bg: "rgba(96,165,250,0.12)", text: "#60a5fa" };

  return (
    <>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex", alignItems: "center", padding: "9px 20px 9px 52px", gap: 12,
          borderBottom: "1px solid rgba(255,255,255,0.04)",
          cursor: "pointer",
          background: expanded ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.02)",
        }}
      >
        <span style={{ flex: 1, fontFamily: "JetBrains Mono", fontSize: 12, color: "#f59e0b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <span style={{ fontSize: 11, marginRight: 4 }}>⚙</span>{span.tool_id}
        </span>
        <span style={{ flex: "0 0 68px", display: "inline-flex", padding: "2px 7px", borderRadius: 3, fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>{span.status || "\u2014"}</span>
        <span style={{ flex: "0 0 60px" }} />
        <span style={{ flex: "0 0 60px" }} />
        <span style={{ flex: "0 0 70px", fontFamily: "JetBrains Mono", fontSize: 11.5, color: "#555" }}>{"\u2014"}</span>
        <span style={{ flex: "0 0 70px", fontFamily: "JetBrains Mono", fontSize: 11.5, color: "#555" }}>
          {span.duration_ms != null ? `${span.duration_ms}ms` : "\u2014"}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: "10px 20px 10px 52px", background: "rgba(245,158,11,0.03)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#f59e0b", marginBottom: 5 }}>Input</div>
          <pre style={{ background: "#0a0a0a", border: "1px solid rgba(245,158,11,0.12)", borderRadius: 4, padding: "10px 12px", fontSize: 10.5, color: "#888", lineHeight: 1.7, whiteSpace: "pre", fontFamily: "inherit", overflowX: "auto", margin: 0 }}>
            {span.tool_input ? (typeof span.tool_input === "string" ? span.tool_input : JSON.stringify(span.tool_input, null, 2)) : "\u2014"}
          </pre>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#f59e0b", marginBottom: 5, marginTop: 8 }}>Output</div>
          <pre style={{ background: "#0a0a0a", border: "1px solid rgba(245,158,11,0.12)", borderRadius: 4, padding: "10px 12px", fontSize: 10.5, color: "#888", lineHeight: 1.7, whiteSpace: "pre", fontFamily: "inherit", overflowX: "auto", margin: 0 }}>
            {span.tool_output ? (typeof span.tool_output === "string" ? span.tool_output : JSON.stringify(span.tool_output, null, 2)) : "\u2014"}
          </pre>
        </div>
      )}
    </>
  );
}

// --- SpanRow ---
function SpanRow({ span }) {
  const [expanded, setExpanded] = React.useState(false);
  const isError = span.status === "error";
  const hasDetail = isError && (span.error_type || span.error_message);

  const statusColors = {
    success: { bg: "rgba(16,185,129,0.13)", text: "#10b981" },
    error: { bg: "rgba(248,113,113,0.12)", text: "#f87171" },
    timeout: { bg: "rgba(245,158,11,0.13)", text: "#f59e0b" },
  };
  const sc = statusColors[span.status] || { bg: "rgba(96,165,250,0.12)", text: "#60a5fa" };

  return (
    <>
      <div
        onClick={() => hasDetail && setExpanded(!expanded)}
        style={{
          display: "flex", alignItems: "center", padding: "9px 20px 9px 52px", gap: 12,
          borderBottom: "1px solid rgba(255,255,255,0.04)",
          cursor: hasDetail ? "pointer" : "default",
          background: expanded ? "rgba(255,255,255,0.02)" : "transparent",
        }}
      >
        <span style={{ flex: 1, fontFamily: "JetBrains Mono", fontSize: 12, color: isError ? "#f87171" : "#c0c0c0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{span.operation || "span"}</span>
        <span style={{ flex: "0 0 68px", display: "inline-flex", padding: "2px 7px", borderRadius: 3, fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>{span.status || "\u2014"}</span>
        <span style={{ flex: "0 0 60px", fontFamily: "JetBrains Mono", fontSize: 11, color: "#555" }}>
          <span style={{ fontSize: 8, color: "#444", display: "block", marginBottom: 1 }}>in</span>
          {span.input_tokens > 0 ? span.input_tokens.toLocaleString() : "\u2014"}
        </span>
        <span style={{ flex: "0 0 60px", fontFamily: "JetBrains Mono", fontSize: 11, color: "#555" }}>
          <span style={{ fontSize: 8, color: "#444", display: "block", marginBottom: 1 }}>out</span>
          {span.output_tokens > 0 ? span.output_tokens.toLocaleString() : "\u2014"}
        </span>
        <span style={{ flex: "0 0 70px", fontFamily: "JetBrains Mono", fontSize: 11.5, color: "#555" }}>
          {span.cost_usd > 0 ? `$${span.cost_usd.toFixed(4)}` : "\u2014"}
        </span>
        <span style={{ flex: "0 0 70px", fontFamily: "JetBrains Mono", fontSize: 11.5, color: "#555" }}>
          {span.duration_ms != null ? `${span.duration_ms}ms` : "\u2014"}
        </span>
      </div>
      {expanded && hasDetail && (
        <div style={{ padding: "10px 20px 10px 52px", background: "rgba(248,113,113,0.04)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#f87171", marginBottom: 6 }}>
            {span.error_type || "error"}
          </div>
          {span.error_message && (
            <div style={{ fontSize: 12.5, color: "rgba(248,113,113,0.7)", lineHeight: 1.6 }}>{span.error_message}</div>
          )}
          {span.stack_trace && (
            <pre style={{ background: "#0a0a0a", border: "1px solid rgba(248,113,113,0.12)", borderRadius: 4, padding: "10px 12px", fontSize: 10.5, color: "rgba(248,113,113,0.5)", lineHeight: 1.7, maxHeight: 200, overflowY: "auto", whiteSpace: "pre", fontFamily: "inherit", marginTop: 6 }}>{span.stack_trace}</pre>
          )}
        </div>
      )}
    </>
  );
}

// --- SpanTree ---
function SpanTree({ traceId }) {
  const { data, isLoading, error } = useSpanTree(traceId, { enabled: true });

  if (isLoading) return <div style={{ padding: "12px 20px", color: "#555", fontFamily: "JetBrains Mono", fontSize: 11 }}>Loading spans...</div>;
  // 404 or missing trace = no SDK-level telemetry for this run (outcome-only mode)
  if (error?.response?.status === 404 || (!isLoading && !data)) return <div style={{ padding: "12px 20px", color: "#555", fontFamily: "JetBrains Mono", fontSize: 11 }}>No step-level telemetry for this run</div>;
  if (error) return <div style={{ padding: "12px 20px", color: "#f87171", fontFamily: "JetBrains Mono", fontSize: 11 }}>Failed to load spans</div>;

  const flattenSpans = (span, depth = 0) => {
    const result = [{ ...span, depth }];
    (span.children || []).forEach(child => result.push(...flattenSpans(child, depth + 1)));
    return result;
  };

  const spans = flattenSpans(data);
  const toolSpans = spans.filter(s => s.tool_id);
  const regularSpans = spans.filter(s => !s.tool_id);

  return (
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
      {/* Column headers */}
      <div style={{ display: "flex", alignItems: "center", padding: "6px 20px 6px 52px", gap: 12, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <span style={{ flex: 1, fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Operation</span>
        <span style={{ flex: "0 0 68px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Status</span>
        <span style={{ flex: "0 0 60px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>In Tok</span>
        <span style={{ flex: "0 0 60px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Out Tok</span>
        <span style={{ flex: "0 0 70px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Cost</span>
        <span style={{ flex: "0 0 70px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Duration</span>
      </div>
      {regularSpans.map((span, i) => (
        <SpanRow key={span.span_id || i} span={span} />
      ))}
      {toolSpans.map((span, i) => (
        <ToolRow key={`tool-${span.span_id || i}`} span={span} />
      ))}
    </div>
  );
}

// --- RunRow ---
function RunRow({ run, outcomeMap }) {
  const [open, setOpen] = React.useState(false);
  const isSuccess = run.outcome_success === true || (!run.has_error && run.outcome_success !== false);
  const failureReason = outcomeMap?.[run.trace_id]?.failure_reason || null;

  const shortId = (run.trace_id || "").slice(0, 8);
  const envLabel = run.environment || "prod";

  const metaFields = [
    { key: "workflow_id", value: run.workflow_id },
    { key: "request_id", value: run.request_id },
    { key: "service", value: run.service },
    { key: "user_id", value: run.user_id },
  ].filter(f => f.value);

  return (
    <div style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: "flex", alignItems: "center", padding: "11px 20px", gap: 12, cursor: "pointer", transition: "background 0.1s", userSelect: "none" }}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.025)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
      >
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: "#555", transition: "transform 0.18s", transform: open ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>{"\u25B6"}</span>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 11.5, color: "#555", flex: "0 0 80px" }}>{shortId}</span>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 12.5, color: "#c0c0c0", flex: "0 0 160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}><ProviderLogo provider={run.root_model} size={14} />{run.root_model || "\u2014"}</span>
        <span style={{
          flex: "0 0 68px", display: "inline-flex", padding: "2px 8px", borderRadius: 3,
          fontFamily: "JetBrains Mono", fontSize: 10.5, fontWeight: 500,
          background: isSuccess ? "rgba(16,185,129,0.13)" : "rgba(248,113,113,0.12)",
          color: isSuccess ? "#10b981" : "#f87171"
        }}>{isSuccess ? "success" : "failure"}</span>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: "#c0c0c0", flex: "0 0 80px" }}>
          {run.total_cost > 0 ? `$${run.total_cost.toFixed(4)}` : "\u2014"}
        </span>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: "#c0c0c0", flex: "0 0 80px" }}>
          {run.total_duration_ms ? `${run.total_duration_ms}ms` : "\u2014"}
        </span>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: "#555", flex: "0 0 50px" }}>{envLabel}</span>
        {!isSuccess && failureReason && (
          <span style={{ fontSize: 11.5, color: "rgba(248,113,113,0.7)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
            {failureReason.slice(0, 70)}
          </span>
        )}
        <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#555", flex: 1, textAlign: "right" }}>
          {run.timestamp ? new Date(run.timestamp).toLocaleTimeString() : "\u2014"}
        </span>
      </div>
      {open && (
        <div style={{ background: "rgba(0,0,0,0.22)" }}>
          {metaFields.length > 0 && (
            <div style={{ display: "flex", gap: 20, padding: "8px 20px 8px 52px", borderBottom: "1px solid rgba(255,255,255,0.04)", flexWrap: "wrap" }}>
              {metaFields.map(f => (
                <div key={f.key} style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: "#444" }}>
                  {f.key}: <span style={{ color: "#666" }}>{f.value}</span>
                </div>
              ))}
            </div>
          )}
          <SpanTree traceId={run.trace_id} />
        </div>
      )}
    </div>
  );
}


// --- HealLog ---
function HealLog({ goal, pipelineId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['pipelineRuns', goal],
    queryFn: () => apiClient.getPipelineRuns(20, goal, 168),
    staleTime: 1000 * 60,
    enabled: !!goal,
  });

  const runs = data?.runs || [];
  const filteredRuns = pipelineId ? runs.filter(r => r.pipeline_id === pipelineId) : runs;
  const runsWithHeals = filteredRuns.filter(r => r.total_heals > 0);

  if (isLoading) return null;
  if (runsWithHeals.length === 0) {
    return (
      <div style={{ padding: '16px', background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, marginTop: 16 }}>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#3f3f46', marginBottom: 8 }}>
          Heal Log
        </div>
        <div style={{ fontSize: 12, color: '#52525b' }}>No heals recorded recently. All steps passing on first attempt.</div>
      </div>
    );
  }

  const LEVER_COLORS = {
    prompt_repair: { bg: 'rgba(167,139,250,0.1)', color: '#a78bfa', label: 'Prompt repaired' },
    dynamic_repair: { bg: 'rgba(167,139,250,0.1)', color: '#a78bfa', label: 'Dynamic repair' },
    model_swap: { bg: 'rgba(249,115,22,0.1)', color: '#f97316', label: 'Model swapped' },
    prompt_variant: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', label: 'Prompt variant' },
  };

  return (
    <div style={{ padding: '16px', background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, marginTop: 16 }}>
      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#3f3f46', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f97316', display: 'inline-block' }}/>
        Heal Log — {runsWithHeals.reduce((s, r) => s + r.total_heals, 0)} automatic interventions
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {runsWithHeals.slice(0, 10).map((run) => (
          <div key={run.run_id} style={{ background: '#111', border: '1px solid rgba(249,115,22,0.15)', borderRadius: 6, padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#f97316' }}>⚡ {run.total_heals} heal{run.total_heals !== 1 ? 's' : ''}</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#3f3f46' }}>{run.total_steps} steps</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: run.success ? '#22c55e' : '#ef4444' }}>{run.success ? 'completed' : 'failed'}</span>
              <span style={{ marginLeft: 'auto', fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: '#3f3f46' }}>
                {run.created_at ? new Date(run.created_at).toLocaleTimeString() : ''}
              </span>
            </div>
            {(run.heal_events || []).map((ev, i) => {
              const leverStyle = LEVER_COLORS[ev.lever_used] || LEVER_COLORS.model_swap;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11.5, color: '#a1a1aa', marginTop: 4 }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: '#52525b', flexShrink: 0, paddingTop: 1 }}>step {ev.step}</span>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: '#52525b', flexShrink: 0, paddingTop: 1 }}>{ev.goal}</span>
                  <span style={{ ...{ padding: '1px 7px', borderRadius: 3, fontSize: 9, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, flexShrink: 0 }, background: leverStyle.bg, color: leverStyle.color }}>
                    {leverStyle.label}
                  </span>
                  {ev.failure_category && (
                    <span style={{ color: '#52525b', fontSize: 10.5 }}>{ev.failure_category}</span>
                  )}
                  {ev.models_tried && ev.models_tried.length > 1 && (
                    <span style={{ color: '#52525b', fontSize: 10.5 }}>
                      {ev.models_tried[0]} → {ev.models_tried[ev.models_tried.length - 1]}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- UserFeedbackTab ---
function UserFeedbackTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['agentFeedback'],
    queryFn: () => apiClient.getAgentFeedback(30),
    staleTime: 1000 * 60 * 2,
  });

  const feedback = data?.feedback || [];

  const formatGoal = (goal) =>
    goal.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const truncate = (str, len = 80) =>
    str.length > len ? str.slice(0, len) + '\u2026' : str;

  if (isLoading) {
    return (
      <div style={{ padding: '40px 28px', textAlign: 'center', color: '#555', fontFamily: 'JetBrains Mono', fontSize: 12 }}>
        Loading feedback signals...
      </div>
    );
  }

  if (feedback.length === 0) {
    return (
      <div style={{ padding: '60px 28px', textAlign: 'center' }}>
        <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>No user feedback signals yet.</div>
        <div style={{ fontSize: 12, color: '#3f3f46' }}>
          Add report_user_turn() to your agent to start capturing behavioral data.
        </div>
      </div>
    );
  }

  // Compute KPIs
  const totalSignals = feedback.reduce((s, f) => s + f.total_signals, 0);
  const totalRejections = feedback.reduce((s, f) => s + f.rejections, 0);
  const totalAcceptances = feedback.reduce((s, f) => s + f.acceptances, 0);
  const avgRepromptRate = totalSignals > 0 ? (totalRejections / totalSignals * 100).toFixed(1) : '0.0';
  const acceptanceRate = totalSignals > 0 ? (totalAcceptances / totalSignals * 100).toFixed(1) : '0.0';

  // SVG trend chart data
  const chartWidth = 600;
  const chartHeight = 140;
  const barPadding = 4;
  const maxBars = Math.min(feedback.length, 12);
  const barWidth = maxBars > 0 ? (chartWidth - barPadding * (maxBars + 1)) / maxBars : 40;
  const maxRate = Math.max(...feedback.slice(0, maxBars).map(f => f.rejection_rate), 0.01);

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        <div style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '18px 20px' }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#555', marginBottom: 10 }}>Avg Re-prompt Rate</div>
          <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.5px', color: '#f87171', fontFamily: 'JetBrains Mono' }}>{avgRepromptRate}%</div>
        </div>
        <div style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '18px 20px' }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#555', marginBottom: 10 }}>Acceptance Rate</div>
          <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.5px', color: '#10b981', fontFamily: 'JetBrains Mono' }}>{acceptanceRate}%</div>
        </div>
      </div>

      {/* Trend chart (SVG) — re-prompt rate by step */}
      <div style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#555', marginBottom: 12 }}>Re-prompt Rate by Step</div>
        <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight + 16}`} style={{ overflow: 'visible' }}>
          {[0, 0.25, 0.5, 0.75, 1].map(pct => {
            const y = chartHeight - pct * (chartHeight - 20);
            return <line key={pct} x1={0} x2={chartWidth} y1={y} y2={y} stroke="rgba(255,255,255,0.04)" />;
          })}
          {feedback.slice(0, maxBars).map((item, i) => {
            const barH = Math.max((item.rejection_rate / maxRate) * (chartHeight - 20), 2);
            const x = barPadding + i * (barWidth + barPadding);
            const y = chartHeight - barH;
            return (
              <g key={item.goal}>
                <rect x={x} y={y} width={barWidth} height={barH} rx={2} fill="#f87171" opacity={0.7} />
                <text x={x + barWidth / 2} y={y - 4} textAnchor="middle" fill="#f87171" fontSize={9} fontFamily="JetBrains Mono">
                  {(item.rejection_rate * 100).toFixed(0)}%
                </text>
                <text x={x + barWidth / 2} y={chartHeight + 12} textAnchor="middle" fill="#555" fontSize={8} fontFamily="JetBrains Mono">
                  {formatGoal(item.goal).slice(0, 10)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Step bar rows */}
      <div style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 20px', gap: 10, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: 11, fontWeight: 500, color: '#d4d4d8' }}>Steps by Re-prompt Rate</span>
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: '#3f3f46' }}>Behavioral signals · last 30 days</span>
        </div>
        {feedback.map((item) => (
          <div key={item.goal} style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: 12, color: '#c0c0c0', flex: '0 0 180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatGoal(item.goal)}
              </span>
              <div style={{ flex: '0 0 120px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: '#f87171', borderRadius: 3, width: `${Math.min(item.rejection_rate * 100, 100)}%` }} />
                </div>
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: '#f87171', minWidth: 32 }}>
                  {(item.rejection_rate * 100).toFixed(0)}%
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {item.closing_sessions > 0 && (
                  <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: 3, fontFamily: 'JetBrains Mono', fontSize: 10, fontWeight: 500, background: 'rgba(16,185,129,0.13)', color: '#10b981' }}>
                    {item.closing_sessions} closing
                  </span>
                )}
                {item.widening_sessions > 0 && (
                  <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: 3, fontFamily: 'JetBrains Mono', fontSize: 10, fontWeight: 500, background: 'rgba(248,113,113,0.12)', color: '#f87171' }}>
                    {item.widening_sessions} widening
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                {(item.top_rejection_dimensions || []).map((dim) => (
                  <span key={dim} style={{ display: 'inline-flex', padding: '2px 6px', borderRadius: 3, fontFamily: 'JetBrains Mono', fontSize: 9, background: 'rgba(255,255,255,0.06)', color: '#71717a' }}>
                    {dim}
                  </span>
                ))}
              </div>
            </div>
            {item.recent_reprompts && item.recent_reprompts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 4 }}>
                {item.recent_reprompts.slice(0, 3).map((rp, rpIdx) => (
                  <span key={rpIdx} style={{ fontFamily: 'JetBrains Mono', fontSize: 10.5, color: '#52525b', lineHeight: 1.5 }}>
                    &ldquo;{truncate(rp)}&rdquo;
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- CostTab ---
function CostTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['savingsEstimate'],
    queryFn: () => apiClient.getSavingsEstimate(),
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <div style={{ padding: '40px 28px', textAlign: 'center', color: '#555', fontFamily: 'JetBrains Mono', fontSize: 12 }}>
        Loading cost data...
      </div>
    );
  }

  if (!data || data.actual_cost_usd === 0) {
    return (
      <div style={{ padding: '60px 28px', textAlign: 'center' }}>
        <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>Run at least one agent task to see cost breakdown.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Big numbers: cost with vs without Kalibr */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        <div style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '24px 20px' }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#555', marginBottom: 12 }}>Cost with Kalibr — Total (last 30d)</div>
          <div style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-1px', color: '#10b981', fontFamily: 'JetBrains Mono' }}>
            ${data.actual_cost_usd.toFixed(2)}
          </div>
        </div>
        <div style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '24px 20px' }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#555', marginBottom: 12 }}>Without Kalibr — Total (last 30d)</div>
          <div style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-1px', color: '#f87171', fontFamily: 'JetBrains Mono' }}>
            ${data.baseline_cost_usd.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Savings KPI */}
      <div style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '18px 20px', marginBottom: 24 }}>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#555', marginBottom: 10 }}>Savings vs Baseline</div>
        <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.5px', color: '#10b981', fontFamily: 'JetBrains Mono' }}>
          {data.savings_pct.toFixed(0)}% saved vs GPT-4o baseline
        </div>
      </div>

      {/* Note */}
      <div style={{ fontSize: 12, color: '#52525b', lineHeight: 1.7, padding: '0 4px' }}>
        Baseline calculated using GPT-4o pricing across all steps. Kalibr routes to cheaper models when quality holds.
      </div>
    </div>
  );
}

// --- Main component ---
export default function AgentDetailV3() {
  const { goal: encodedGoal } = useParams();
  const goal = decodeURIComponent(encodedGoal);
  const [timeRange, setTimeRange] = React.useState(168);
  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedPipelineId, setSelectedPipelineId] = React.useState('');

  const { data: goalPolicyData, isLoading: policyLoading } = useGoalPolicy(goal);

  const { data: executionsData, isLoading: execLoading } = useQuery({
    queryKey: ["goalExecutionsV3", goal, timeRange],
    queryFn: () => apiClient.getExecutionsByGoal(goal, timeRange, 100),
    staleTime: 1000 * 30,
    enabled: !!goal,
  });

  const { data: outcomesData } = useGoalOutcomes(goal, 100);

  const outcomeMap = React.useMemo(() => {
    const map = {};
    (outcomesData?.outcomes || []).forEach(o => { map[o.trace_id] = o; });
    return map;
  }, [outcomesData]);

  // Pipeline runs for heal count KPI (Execution tab)
  const { data: healData } = useQuery({
    queryKey: ['pipelineRunsTab', goal],
    queryFn: () => apiClient.getPipelineRuns(50, goal, 168),
    staleTime: 1000 * 60,
    enabled: !!goal,
  });

  const runs = executionsData?.executions || [];

  const stats = goalPolicyData?.stats;
  const policy = goalPolicyData?.policy;

  const totalRuns = runs.length;
  const successRuns = runs.filter(r => r.outcome_success === true).length;
  const successRate = totalRuns > 0 ? ((successRuns / totalRuns) * 100).toFixed(1) : "\u2014";

  const pipelineIds = React.useMemo(() => {
    const ids = new Set();
    (healData?.runs || []).forEach(r => {
      if (r.pipeline_id) ids.add(r.pipeline_id);
    });
    return Array.from(ids).sort();
  }, [healData]);

  const filteredHealRuns = React.useMemo(() => {
    const allRuns = healData?.runs || [];
    return selectedPipelineId ? allRuns.filter(r => r.pipeline_id === selectedPipelineId) : allRuns;
  }, [healData, selectedPipelineId]);

  const healRuns = filteredHealRuns.filter(r => r.total_heals > 0);
  const totalHeals = healRuns.reduce((s, r) => s + r.total_heals, 0);

  const pageStatus = stats?.status || "exploring";

  const tabs = ['User Feedback', 'Execution', 'Cost'];

  return (
    <SidebarLayout>
      <div style={{ background: "#020202", minHeight: "100vh" }}>
        {/* Header */}
        <div style={{ height: 52, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "#0d0d0d" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Link to="/agents" style={{ color: "#555", textDecoration: "none", fontFamily: "JetBrains Mono", fontSize: 11 }}>{"\u2190 agents"}</Link>
            <span style={{ color: "rgba(255,255,255,0.2)" }}>{"\u00B7"}</span>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: 14, fontWeight: 500, color: "#f2f2f2" }}>{goal}</span>
            <GoalStatusBadge status={pageStatus} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 4, overflow: "hidden" }}>
              {[{ label: "24h", val: 24 }, { label: "7d", val: 168 }, { label: "30d", val: 720 }].map(({ label, val }) => (
                <button key={val} onClick={() => setTimeRange(val)} style={{ padding: "0 12px", height: 28, fontSize: 12, fontFamily: "JetBrains Mono", color: timeRange === val ? "#f2f2f2" : "#555", background: timeRange === val ? "#141414" : "transparent", border: "none", borderRight: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", transition: "all 0.1s" }}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0d0d0d', padding: '0 28px' }}>
          {tabs.map((label, i) => (
            <button
              key={label}
              onClick={() => setActiveTab(i)}
              style={{
                padding: '10px 20px',
                fontFamily: 'JetBrains Mono',
                fontSize: 12,
                color: activeTab === i ? '#f2f2f2' : '#555',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === i ? '2px solid #f2f2f2' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'color 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* User Feedback tab */}
        {activeTab === 0 && <UserFeedbackTab />}

        {/* Execution tab */}
        {activeTab === 1 && (
          <>
            <div style={{ padding: "24px 28px" }}>
              {/* Execution KPI cards */}
              {!policyLoading && !execLoading && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
                  {[
                    { label: "Total Heals", value: totalHeals.toLocaleString(), color: "#f97316" },
                    { label: "Success Rate", value: `${successRate}%`, color: "#10b981", sub: "structural pass rate" },
                    { label: "Total Runs", value: totalRuns.toLocaleString() },
                  ].map(({ label, value, color, sub }) => (
                    <div key={label} style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 7, padding: "18px 20px" }}>
                      <div style={{ fontFamily: "JetBrains Mono", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555", marginBottom: 10 }}>{label}</div>
                      <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.5px", color: color || "#f2f2f2" }}>{value}</div>
                      {sub && <div style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: "#3f3f46", marginTop: 4 }}>{sub}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Routing decision */}
              <RoutingCallout policy={policy} stats={stats} />

              {/* Path performance table — reliability vs eval score */}
              {stats?.paths && stats.paths.length > 0 && (
                <div style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, overflow: "hidden", marginBottom: 20 }}>
                  {/* Header */}
                  <div style={{ display: "flex", alignItems: "center", padding: "10px 20px", gap: 10, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, fontWeight: 500, color: "#d4d4d8" }}>Path Performance</span>
                    <div style={{ flex: 1 }} />
                    <span style={{ fontFamily: "JetBrains Mono", fontSize: 9, color: "#3f3f46" }}>Reliability = structural pass rate &nbsp;·&nbsp; Eval Score = LLM judge quality (0–1)</span>
                  </div>
                  {/* Column headers */}
                  <div style={{ display: "grid", gridTemplateColumns: "1.8fr 80px 90px 90px 90px 80px 90px", gap: 8, padding: "6px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    {["Model", "Decisions", "Reliability", "Eval Score", "Avg Cost", "Status", "Mode"].map(h => (
                      <span key={h} style={{ fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>{h}</span>
                    ))}
                  </div>
                  {/* Path rows */}
                  {[...stats.paths]
                    .sort((a, b) => (b.sample_count || 0) - (a.sample_count || 0))
                    .map(path => {
                      const reliability = path.success_rate != null ? `${(path.success_rate * 100).toFixed(1)}%` : "\u2014";
                      const reliabilityColor = path.success_rate == null ? "#555"
                        : path.success_rate >= 0.9 ? "#10b981"
                        : path.success_rate >= 0.7 ? "#f59e0b"
                        : "#f87171";
                      const evalScore = path.avg_score != null ? path.avg_score.toFixed(2) : "\u2014";
                      const evalColor = path.avg_score == null ? "#555"
                        : path.avg_score >= 0.8 ? "#10b981"
                        : path.avg_score >= 0.6 ? "#f59e0b"
                        : "#f87171";
                      const statusMap = {
                        leader: { label: "Leader", bg: "rgba(16,185,129,0.13)", color: "#10b981" },
                        active: { label: "Active", bg: "rgba(96,165,250,0.13)", color: "#60a5fa" },
                        testing: { label: "Testing", bg: "rgba(96,165,250,0.13)", color: "#60a5fa" },
                        low_data: { label: "Learning", bg: "rgba(96,165,250,0.13)", color: "#60a5fa" },
                        benched: { label: "Benched", bg: "rgba(255,255,255,0.06)", color: "#71717a" },
                      };
                      const statusStyle = statusMap[path.status] || statusMap.testing;
                      const modelColor = path.status === "leader" ? "#10b981" : path.status === "benched" ? "#52525b" : "#c0c0c0";
                      const modeLabel = path.status === "leader" ? "Exploitation" : path.status === "benched" ? "\u2014" : "Exploration";

                      return (
                        <div key={path.path_id || path.model_id} style={{ display: "grid", gridTemplateColumns: "1.8fr 80px 90px 90px 90px 80px 90px", gap: 8, padding: "9px 20px", borderBottom: "1px solid rgba(255,255,255,0.03)", alignItems: "center" }}>
                          <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: modelColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{path.model_id}</span>
                          <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: "#a1a1aa" }}>{(path.sample_count || 0).toLocaleString()}</span>
                          <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: reliabilityColor }}>{reliability}</span>
                          <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: evalColor }} title="LLM judge quality score — avg output quality across scored runs">{evalScore}</span>
                          <span style={{ fontFamily: "JetBrains Mono", fontSize: 12, color: "#555" }}>{path.avg_cost_usd > 0 ? `$${path.avg_cost_usd.toFixed(4)}` : "\u2014"}</span>
                          <span style={{ display: "inline-flex", padding: "2px 7px", borderRadius: 3, fontFamily: "JetBrains Mono", fontSize: 10, fontWeight: 500, background: statusStyle.bg, color: statusStyle.color, width: "fit-content" }}>{statusStyle.label}</span>
                          <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#52525b" }}>{modeLabel}</span>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Runs accordion */}
              <div style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, overflow: "hidden", marginBottom: 20 }}>
                {/* Column headers */}
                <div style={{ display: "flex", alignItems: "center", padding: "10px 20px", gap: 12, borderBottom: "1px solid rgba(255,255,255,0.08)", background: "#0d0d0d" }}>
                  <span style={{ width: 18 }}></span>
                  <span style={{ flex: "0 0 80px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Run ID</span>
                  <span style={{ flex: "0 0 160px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Model</span>
                  <span style={{ flex: "0 0 68px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Result</span>
                  <span style={{ flex: "0 0 80px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Cost</span>
                  <span style={{ flex: "0 0 80px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Latency</span>
                  <span style={{ flex: "0 0 50px", fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555" }}>Env</span>
                  <span style={{ flex: 1, fontFamily: "JetBrains Mono", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em", color: "#555", textAlign: "right" }}>Time</span>
                </div>

                {execLoading && (
                  <div style={{ padding: "40px 20px", textAlign: "center", color: "#555", fontFamily: "JetBrains Mono", fontSize: 12 }}>Loading runs...</div>
                )}

                {!execLoading && runs.length === 0 && (
                  <div style={{ padding: "40px 20px", textAlign: "center", color: "#555", fontFamily: "JetBrains Mono", fontSize: 12 }}>No runs found for this time range</div>
                )}

                {!execLoading && runs.map((run) => (
                  <RunRow key={run.trace_id} run={run} outcomeMap={outcomeMap} />
                ))}
              </div>

              {/* Pipeline filter */}
              {pipelineIds.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#555' }}>Pipeline</span>
                  <select
                    value={selectedPipelineId}
                    onChange={e => setSelectedPipelineId(e.target.value)}
                    style={{
                      background: '#0d0d0d',
                      color: '#c0c0c0',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 4,
                      padding: '6px 10px',
                      fontSize: 12,
                      fontFamily: 'JetBrains Mono',
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="">All pipelines</option>
                    {pipelineIds.map(id => (
                      <option key={id} value={id}>{id}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Heal Log */}
              <HealLog goal={goal} pipelineId={selectedPipelineId} />
            </div>
          </>
        )}

        {/* Cost tab */}
        {activeTab === 2 && <CostTab />}
      </div>
    </SidebarLayout>
  );
}
